import React, { useState } from 'react';
import { Button, Dropdown, Menu, Switch, Modal, Radio, Space, message } from 'antd';
import { 
  BellOutlined, 
  BellFilled, 
  SettingOutlined,
  SoundOutlined,
  NotificationOutlined
} from '@ant-design/icons';
import notificationService, { 
  AVAILABLE_SOUNDS, 
  RoomNotificationSettings 
} from '../../services/NotificationService';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

interface NotificationSettingsProps {
  roomId: string;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ roomId }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<RoomNotificationSettings>(
    notificationService.getRoomSettings(roomId)
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  // Переключение звуковых уведомлений
  const handleToggleSound = () => {
    const newEnabled = notificationService.toggleSoundNotifications(roomId);
    setSettings({ ...settings, enabled: newEnabled });
  };
  
  // Открытие модального окна с настройками
  const showSettingsModal = () => {
    setIsModalVisible(true);
  };
  
  // Закрытие модального окна
  const closeModal = () => {
    setIsModalVisible(false);
  };
  
  // Изменение звука уведомлений
  const handleSoundChange = (e: any) => {
    const newSoundUrl = e.target.value;
    notificationService.changeSoundForRoom(roomId, newSoundUrl);
    setSettings({ ...settings, soundUrl: newSoundUrl });
  };
  
  // Воспроизведение звука для предпросмотра
  const playSound = async (soundUrl: string) => {
    try {
      console.log(`Testing sound: ${soundUrl}`);
      const audio = new Audio(soundUrl);
      
      // Дожидаемся загрузки аудио перед воспроизведением
      await new Promise((resolve, reject) => {
        audio.addEventListener('canplaythrough', resolve, { once: true });
        audio.addEventListener('error', (e) => {
          console.error('Error loading sound for preview:', e);
          reject(new Error('Failed to load sound'));
        }, { once: true });
        
        // Инициируем загрузку
        audio.load();
      });
      
      // Воспроизводим звук
      await audio.play();
      console.log('Sound test started successfully');
    } catch (error) {
      console.error('Failed to play test sound:', error);
      message.error('Не удалось воспроизвести звук');
    }
  };
  
  // Определяем текущий выбранный звук
  const getCurrentSoundName = () => {
    const entry = Object.entries(AVAILABLE_SOUNDS).find(([, url]) => url === settings.soundUrl);
    return entry ? entry[0].charAt(0).toUpperCase() + entry[0].slice(1) : 'Default';
  };
  
  // Создаем элементы меню для Dropdown
  const menuItems: MenuProps['items'] = [
    {
      key: 'toggle',
      onClick: handleToggleSound,
      label: (
        <Space>
          {settings.enabled ? <BellFilled /> : <BellOutlined />}
          {settings.enabled ? t('notificationSettings.disableSound') : t('notificationSettings.enableSound')}
        </Space>
      )
    },
    {
      key: 'settings',
      onClick: showSettingsModal,
      label: (
        <Space>
          <SettingOutlined />
          {t('notificationSettings.title')}
        </Space>
      )
    }
  ];
  
  return (
    <>
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
        <Button 
          type="text" 
          icon={settings.enabled ? <BellFilled /> : <BellOutlined />}
          className="notification-settings-button"
        />
      </Dropdown>
      
      <Modal
        title="Настройки уведомлений"
        open={isModalVisible}
        onCancel={closeModal}
        footer={[
          <Button key="close" onClick={closeModal}>
            {t('notificationSettings.close')}
          </Button>
        ]}
      >
        <div className="notification-settings-container">
          <div className="notification-setting-item">
            <div className="setting-label">
              <BellOutlined /> {t('notificationSettings.soundNotifications')}
            </div>
            <Switch 
              checked={settings.enabled} 
              onChange={handleToggleSound} 
            />
          </div>
          
          <div className="notification-setting-item">
            <div className="setting-label">
              <SoundOutlined /> {t('notificationSettings.soundSelection')}
            </div>
            <Radio.Group 
              onChange={handleSoundChange} 
              value={settings.soundUrl}
              disabled={!settings.enabled}
            >
              <Space direction="vertical">
                {Object.entries(AVAILABLE_SOUNDS).map(([name, url]) => (
                  <div key={name} className="sound-option">
                    <Radio value={url}>
                      {name.charAt(0).toUpperCase() + name.slice(1)}
                    </Radio>
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<SoundOutlined />} 
                      onClick={() => playSound(url)} 
                      disabled={!settings.enabled}
                    >
                      {t('notificationSettings.listen')}
                    </Button>
                  </div>
                ))}
              </Space>
            </Radio.Group>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default NotificationSettings; 